import React, { useEffect, useState, useRef } from "react";
import "./styles.css"; 
import Box from "../Box"; 
import TextContent from "./TextContent";
import HourGlass from "./HourGlass";
import LoadingDots from "./LoadingDots";
import PageTitle from "../PageTitle";
import Spacer from "../Spacer";  

interface WaitingRoomProps {
  prolificId: string;
  onGameReady: () => void;
}

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

const WaitingRoom: React.FC<WaitingRoomProps> = ({ prolificId, onGameReady }) => {
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<string>('Checking...');
  const [lastStatusCheck, setLastStatusCheck] = useState<string>('Never');
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const statusIntervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const statusCheckCounterRef = useRef<number>(0);
  const hasPlayedDingRef = useRef<boolean>(false);

  // Debug logging for props
  console.log('WaitingRoom: Component mounted with prolificId:', prolificId);
  console.log('WaitingRoom: prolificId type:', typeof prolificId);
  console.log('WaitingRoom: prolificId length:', prolificId?.length);

  useEffect(() => {
    if (!prolificId) {
      console.log("WaitingRoom: Prolific ID prop is missing. Polling will not start.");
      setPollingError("Cannot check game status: User ID not provided. Please ensure ?prolificID=YOUR_ID is in the URL.");
      return;
    }
    
    if (prolificId.trim() === "") {
      console.log("WaitingRoom: Prolific ID prop is empty string. Polling will not start.");
      setPollingError("Cannot check game status: User ID is empty. Please ensure ?prolificID=YOUR_ID is in the URL.");
      return;
    }
    
    if (!API_BASE_URL) {
      console.error("WaitingRoom: ❌ API_BASE_URL is not set! Status checks will fail!");
      console.error("WaitingRoom: Please set REACT_APP_API_BASE_URL environment variable");
      setPollingError("API_BASE_URL not configured. Please set REACT_APP_API_BASE_URL environment variable.");
      return;
    } else {
      console.log("WaitingRoom: ✅ API_BASE_URL is set:", API_BASE_URL);
    }

    const checkPlayerStatus = async () => {
      statusCheckCounterRef.current += 1;
      console.log(`WaitingRoom: Status check #${statusCheckCounterRef.current} for ${prolificId}...`);
      console.log(`WaitingRoom: API_BASE_URL is: "${API_BASE_URL}"`);
      console.log(`WaitingRoom: Full URL: "${API_BASE_URL}/checkPlayerStatus"`);
      console.log(`WaitingRoom: Making POST request to checkPlayerStatus endpoint...`);

      try {
        const response = await fetch(`${API_BASE_URL}/checkPlayerStatus`, { 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
        });

        console.log(`WaitingRoom: Response status: ${response.status}`);
        console.log(`WaitingRoom: Response headers:`, Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
          const errorText = await response.text();
          console.error('WaitingRoom: Player status check failed with status:', response.status);
          console.error('WaitingRoom: Error response:', errorText);
          
          // If endpoint doesn't exist (404), log a warning but don't treat as error
          if (response.status === 404) {
            console.warn('WaitingRoom: /checkPlayerStatus endpoint not found. This is expected if backend is not updated yet.');
          }
        } else {
          const data = await response.json();
          console.log('WaitingRoom: Player status check successful:', data);
          console.log('WaitingRoom: ✅ lastSeen timestamp should be updated in backend');
          setLastStatusCheck(new Date().toLocaleTimeString());
        }
      } catch (error) {
        console.error('WaitingRoom: Error checking player status:', error);
        console.error('WaitingRoom: Error details:', {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        
        // If we can't reach the backend, we should stop the interval
        // This prevents the frontend from continuing to "appear active" when it's not
        console.error('WaitingRoom: Network error - stopping status checks to prevent false active status');
        if (statusIntervalIdRef.current) {
          clearInterval(statusIntervalIdRef.current);
          statusIntervalIdRef.current = null;
        }
      }
    };

    const checkGameStatus = async () => {
      console.log(`WaitingRoom: Checking game status for ${prolificId}...`);

      try {
        const response = await fetch(`${API_BASE_URL}/isGameReady`, { 
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
        });

        const data = await response.json(); 

        if (!response.ok) {
          // Safely convert error to string, handling cases where it might be an object
          const errorText = data.message || data.error;
          const errorString = typeof errorText === 'string' ? errorText : JSON.stringify(errorText);
          throw new Error(errorString || `HTTP error! Status: ${response.status}`);
        }
        
        console.log('WaitingRoom: Received game status:', data);

        if (data.isGameReady && !hasPlayedDingRef.current) {
          console.log('WaitingRoom: Game is ready! Calling onGameReady().');
          hasPlayedDingRef.current = true; // Prevent multiple plays
          
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          if (statusIntervalIdRef.current) {
            clearInterval(statusIntervalIdRef.current);
            statusIntervalIdRef.current = null;
          }
          setPollingError(null);
          
          // Play 3 loud dings when partner is matched
          const playDing = () => {
            try {
              const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
              const audioContext = new AudioContextClass();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              
              oscillator.frequency.value = 800; // High pitch ding
              oscillator.type = 'sine';
              
              // Maximum volume (1.0 = 100% volume)
              gainNode.gain.setValueAtTime(0, audioContext.currentTime);
              gainNode.gain.linearRampToValueAtTime(1.0, audioContext.currentTime + 0.01);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
              
              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.2);
            } catch (error) {
              console.warn('WaitingRoom: Could not play ding sound:', error);
            }
          };
          
          // Play 3 dings with delays (300ms apart)
          playDing();
          setTimeout(() => playDing(), 300);
          setTimeout(() => playDing(), 600);
          
          onGameReady();
        }
      } catch (error) {
        console.error('WaitingRoom: Error checking game status:', error);
        const errorMessage = error instanceof Error ? error.message : "An unknown error occurred."
        setPollingError(`Failed to get game status: ${errorMessage} Retrying...`);
      }
    };

    checkGameStatus();
    checkPlayerStatus(); // Initial status check
    intervalIdRef.current = setInterval(checkGameStatus, 1000); 
    statusIntervalIdRef.current = setInterval(checkPlayerStatus, 5000); // Heartbeat every 5s while waiting
    
    console.log('WaitingRoom: Intervals started');
    console.log('WaitingRoom: Game status interval ID:', intervalIdRef.current);
    console.log('WaitingRoom: Player status interval ID:', statusIntervalIdRef.current);
    
    // Test the endpoint immediately to verify it exists
    console.log('WaitingRoom: Testing /checkPlayerStatus endpoint...');
    console.log('WaitingRoom: Test URL:', `${API_BASE_URL}/checkPlayerStatus`);
    
    fetch(`${API_BASE_URL}/checkPlayerStatus`, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'prolific-id': prolificId,
      },
    })
    .then(response => {
      console.log('WaitingRoom: Test call response status:', response.status);
      console.log('WaitingRoom: Test call response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        console.log('WaitingRoom: ✅ /checkPlayerStatus endpoint is working!');
        setApiStatus('✅ API Connected');
        return response.json();
      } else {
        console.log('WaitingRoom: ❌ /checkPlayerStatus endpoint returned:', response.status);
        setApiStatus(`❌ API Error: ${response.status}`);
        return response.text();
      }
    })
    .then(data => {
      console.log('WaitingRoom: Test call response data:', data);
    })
    .catch(error => {
      console.log('WaitingRoom: ❌ /checkPlayerStatus endpoint test failed:', error.message);
      console.log('WaitingRoom: Error type:', error.name);
      console.log('WaitingRoom: This might be a CORS issue or network problem');
      setApiStatus(`❌ Network Error: ${error.message}`);
    });

    return () => { 
      if (intervalIdRef.current) {
        console.log("WaitingRoom: Clearing game status polling interval.");
        clearInterval(intervalIdRef.current);
      }
      if (statusIntervalIdRef.current) {
        console.log("WaitingRoom: Clearing player status polling interval.");
        clearInterval(statusIntervalIdRef.current);
      }
    };
  }, [prolificId, onGameReady]);

  // Additional cleanup for browser events
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('WaitingRoom: beforeunload event - clearing intervals');
      if (statusIntervalIdRef.current) {
        clearInterval(statusIntervalIdRef.current);
        statusIntervalIdRef.current = null;
      }
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };

    const handlePageHide = () => {
      console.log('WaitingRoom: pagehide event - clearing intervals');
      if (statusIntervalIdRef.current) {
        clearInterval(statusIntervalIdRef.current);
        statusIntervalIdRef.current = null;
      }
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('WaitingRoom: page became hidden - keeping intervals active');
        // Do not clear intervals; keep heartbeat and readiness polling running
      }
    };

    const handleFocus = () => {
      console.log('WaitingRoom: window lost focus - keeping intervals active');
      // Do not clear intervals on blur; keep polling to avoid desync
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleFocus);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleFocus);
    };
  }, []);

  return (
    <>
      <div className="container"> 
        <PageTitle title="Waiting Room" />
        <Spacer />
        <Box>
          <>
            <TextContent /> 
            <Spacer />
            <HourGlass />
            <Spacer />
            <LoadingDots />
            {/* <Spacer />
            <p style={{ color: 'blue', textAlign: 'center', fontSize: '0.9em' }}>
              API Status: {apiStatus}
            </p>
            <p style={{ color: 'green', textAlign: 'center', fontSize: '0.8em' }}>
              Last Status Check: {lastStatusCheck}
            </p> */}
            {pollingError && (
              <>
                <Spacer />
                <p style={{ color: 'orange', textAlign: 'center', fontSize: '0.9em' }}>
                  {pollingError}
                </p>
              </>
            )}
          </>
        </Box>
      </div>
    </>
  );
}

export default React.memo(WaitingRoom);